import React, { useState, useEffect } from 'react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

// Create a SweetAlert instance that can handle React content
const ReactSwal = withReactContent(Swal);

// Small delay to ensure the tooltip click event completes before showing the alert
const MOBILE_ALERT_DELAY = 50;

/**
 * TippyWrapper - A React wrapper component for Tippy.js with SweetAlert fallback on mobile
 *
 * This component provides tooltip functionality using @tippyjs/react with automatic
 * SweetAlert modal fallback for mobile devices (screens ≤639px).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The element that triggers the tooltip
 * @param {React.ReactNode} props.content - The tooltip content (React component or element)
 * @param {string} props.placement - Tooltip placement (default: 'top')
 * @param {boolean} props.useMobileFallback - Enable SweetAlert fallback on mobile (default: false)
 * @param {Object} props.tippyProps - Additional props to pass to Tippy component
 */
const TippyWrapper = ({
  children,
  content,
  placement = 'top',
  useMobileFallback = false,
  mobileClose,
  ...tippyProps
}) => {
  const [isMobileAndOpen, setIsMobileAndOpen] = useState(false);
  useEffect(() => {
    // Close the tooltip if the mobile alert is open
    if (isMobileAndOpen) {
      // We need to update Swal since content changed
      ReactSwal.update({
        html: content,
      });
    }
  }, [content]);

  const handleShow = (instance) => {
    // Check if we're on mobile and should use the fallback
    if (useMobileFallback && window.matchMedia('(max-width: 639px)').matches) {
      // Show SweetAlert instead of tooltip on mobile
      setTimeout(async () => {
        setIsMobileAndOpen(true);
        const f = await ReactSwal.fire({
          html: content,
          animation: false,
          confirmButtonText: 'Close',
        });
        setIsMobileAndOpen(false);

        // Notify parent component
        if (mobileClose) mobileClose();
      }, MOBILE_ALERT_DELAY);

      // Return false to prevent tooltip from showing
      return false;
    }
  };

  return (
    <Tippy
      content={content}
      allowHTML={typeof content === 'string'}
      theme='hurryup'
      placement={placement}
      interactive={true}
      onShow={handleShow}
      appendTo={() => document.body}
      {...tippyProps}
    >
      {children}
    </Tippy>
  );
};

export default TippyWrapper;
